import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * T-0113 — modal stack context.
 *
 * Acceptance criterion §2.3: "only one modal visible at a time; opening
 * a new one closes the previous one cleanly". This context owns the
 * single-modal invariant. Every modal in `apps/web/src/components/modals/`
 * that uses an imperative open / close affordance (rather than
 * compositional `open` props) flows through this provider.
 *
 * Why a single state slot
 * -----------------------
 * The prototype's `data-open-*` system is implicitly a stack of one —
 * `modal-root.innerHTML = ''` between openers wipes any prior modal. We
 * port that semantic directly: opening a new modal replaces whatever was
 * open. Callers that need a sequence (override → re-eval tray) are
 * responsible for closing the first modal before opening the second
 * (the override modal's `onSaved` handler does exactly that).
 *
 * Why `openModal` accepts a render-prop callback
 * ----------------------------------------------
 * The provider doesn't know what props each modal needs — `UploadModal`
 * needs `engagementId + projectId`, `OverrideModal` needs `entryId`, etc.
 * Each opener supplies a callback that takes a `dismiss()` handle and
 * returns the JSX. The provider renders that JSX inside the modal slot
 * and passes the dismiss handle through so `<Dialog.Close>` can close
 * the active modal.
 *
 * Why we mount the modal inside the provider's `children` tree
 * ------------------------------------------------------------
 * Radix `Dialog` uses a portal so the actual mount point doesn't matter
 * for visual layout — but React Query's `QueryClientProvider`, the
 * router context, and auth/store all live at the app root. Mounting
 * the active modal as a sibling of `children` inside the provider keeps
 * those contexts available without re-providing them.
 */

export type ModalRenderer = (api: { dismiss: () => void }) => ReactNode;

interface ModalContextValue {
  /**
   * Open a new modal. Replaces any currently-open modal — the acceptance
   * criterion's single-modal invariant is enforced here.
   * Returns a stable dismiss handle the caller can store if they need
   * to programmatically close (e.g. an external "navigate-away" guard).
   */
  openModal: (renderer: ModalRenderer) => () => void;
  /** Close whatever modal is currently open. No-op when nothing is open. */
  closeModal: () => void;
  /** True iff a modal is currently open. Used by the `<button data-open-*>` triggers
   *  to suppress double-open clicks. */
  isOpen: boolean;
}

const ModalContext = createContext<ModalContextValue | null>(null);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [renderer, setRenderer] = useState<ModalRenderer | null>(null);
  // Bump on each open so a stale dismiss-handle returned from a previous
  // open() call can't accidentally close a newer modal.
  const generationRef = useRef(0);

  const closeModal = useCallback(() => {
    setRenderer(null);
  }, []);

  const openModal = useCallback(
    (next: ModalRenderer) => {
      const gen = ++generationRef.current;
      // React doesn't accept a function value via `useState` setter without
      // the callback form (it would treat it as an updater). Box the
      // renderer in an outer thunk to defeat that.
      setRenderer(() => next);
      return () => {
        if (gen === generationRef.current) closeModal();
      };
    },
    [closeModal],
  );

  const value = useMemo<ModalContextValue>(
    () => ({ openModal, closeModal, isOpen: renderer != null }),
    [openModal, closeModal, renderer],
  );

  return (
    <ModalContext.Provider value={value}>
      {children}
      {renderer ? renderer({ dismiss: closeModal }) : null}
    </ModalContext.Provider>
  );
}

/**
 * Hook for components that need to open / close modals imperatively.
 * Throws when used outside the provider — every consumer must mount
 * under the provider; the provider lives at the app root.
 */
export function useModal(): ModalContextValue {
  const ctx = useContext(ModalContext);
  if (!ctx) {
    throw new Error('useModal must be used inside <ModalProvider>.');
  }
  return ctx;
}
